import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { usePageTitle } from '../../lib/usePageTitle';
import { useAnnouncer } from '../../components/LiveAnnouncer';
import { AdminLayout } from './AdminLayout';
import { Button } from '../../components/Button';
import { api, ApiError } from '../../lib/api';

interface AdminLeagueRow {
  id: string;
  name: string;
  status: string;
  owner: string;
  ownerEmail: string;
  competition: string;
  members: number;
  matchdays: number;
  createdAt: string;
}

export function AdminLeaguesPage() {
  usePageTitle('Leghe — Amministrazione');
  const { announce } = useAnnouncer();
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);

  const leagues = useQuery({
    queryKey: ['admin', 'leagues'],
    queryFn: () => api<{ leagues: AdminLeagueRow[] }>('/admin/leagues').then((r) => r.leagues),
  });

  const setStatus = async (league: AdminLeagueRow, status: string, message: string) => {
    setError(null);
    const reason = window.prompt('Motivo dell’operazione (obbligatorio, finisce nell’audit log):');
    if (!reason || reason.trim().length < 3) {
      announce('Operazione annullata: serve un motivo.', 'assertive');
      return;
    }
    try {
      await api(`/admin/leagues/${league.id}`, {
        method: 'PATCH',
        body: { status, reason: reason.trim() },
      });
      announce(message);
      queryClient.invalidateQueries({ queryKey: ['admin', 'leagues'] });
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : 'Operazione non riuscita.';
      setError(msg);
      announce(`Errore: ${msg}`, 'assertive');
    }
  };

  return (
    <AdminLayout title="Leghe">
      {error && (
        <p role="alert" className="mt-4 font-medium text-danger">{error}</p>
      )}
      {leagues.isLoading && (
        <p role="status" className="mt-6">Caricamento leghe…</p>
      )}
      {leagues.data && (
        <div className="mt-6 overflow-x-auto">
          <table className="w-full border-collapse text-left">
            <caption className="visually-hidden">
              Tutte le leghe della piattaforma con proprietario, membri e azioni
            </caption>
            <thead>
              <tr className="border-b-2 border-ink-300 dark:border-ink-600">
                <th scope="col" className="py-2 pr-3">Lega</th>
                <th scope="col" className="py-2 pr-3">Proprietario</th>
                <th scope="col" className="py-2 pr-3">Stato</th>
                <th scope="col" className="py-2 pr-3 text-right">Membri</th>
                <th scope="col" className="py-2 pr-3 text-right">Giornate</th>
                <th scope="col" className="py-2">Azioni</th>
              </tr>
            </thead>
            <tbody>
              {leagues.data.map((l) => (
                <tr key={l.id} className="border-b border-ink-200 dark:border-ink-800">
                  <th scope="row" className="py-3 pr-3 font-medium">
                    {l.name}
                    <span className="block text-sm font-normal text-ink-600 dark:text-ink-300">
                      {l.competition}
                    </span>
                  </th>
                  <td className="py-3 pr-3">
                    {l.owner}
                    <span className="block text-sm text-ink-600 dark:text-ink-300">{l.ownerEmail}</span>
                  </td>
                  <td className="py-3 pr-3">
                    {l.status === 'ACTIVE' ? 'Attiva' : l.status === 'SUSPENDED' ? 'Sospesa' : l.status}
                  </td>
                  <td className="py-3 pr-3 text-right">{l.members}</td>
                  <td className="py-3 pr-3 text-right">{l.matchdays}</td>
                  <td className="py-3">
                    <span className="flex flex-wrap gap-2">
                      {l.status === 'ACTIVE' ? (
                        <Button
                          variant="danger"
                          onClick={() => setStatus(l, 'SUSPENDED', `Lega ${l.name} sospesa.`)}
                        >
                          Sospendi
                          <span className="visually-hidden"> la lega {l.name}</span>
                        </Button>
                      ) : (
                        <Button
                          variant="secondary"
                          onClick={() => setStatus(l, 'ACTIVE', `Lega ${l.name} riattivata.`)}
                        >
                          Riattiva
                          <span className="visually-hidden"> la lega {l.name}</span>
                        </Button>
                      )}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </AdminLayout>
  );
}
